import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { FeaturePhotoPoint } from 'meta/photo/photoPoint'
import { Project } from 'meta/project/project'

import { LayerIds, SourceIds } from 'client/components/Map/ids'
import { MapSnapshot, PropsMapHook } from 'client/components/Map/types'

type Props = PropsMapHook & {
  projectName: Project['name']
}

export const useOnClick = (props: Props): void => {
  const { loaded, mapRef, projectName, snapshotRef } = props

  const navigate = useNavigate()

  useEffect(() => {
    if (loaded) {
      const map = mapRef.current!.getMap()

      map.on('click', [LayerIds.clusterStars, LayerIds.unclusteredPoint], async (e) => {
        const featureClicked = e.features?.[0]

        if (!featureClicked) {
          return
        }

        const features: Array<FeaturePhotoPoint> = []

        if (featureClicked.properties?.cluster && featureClicked.properties?.cluster_id) {
          const clusterId = featureClicked.properties.cluster_id
          const source = map.getSource(SourceIds.photoPoints) as mapboxgl.GeoJSONSource

          await new Promise<void>((resolve, reject) => {
            source.getClusterLeaves(
              clusterId,
              Infinity, // max number of features
              0, // offset
              (err, clusterFeatures) => {
                if (err) {
                  reject(err)
                }
                features.push(...(clusterFeatures as unknown as Array<FeaturePhotoPoint>))
                resolve()
              }
            )
          })
        } else {
          features.push(featureClicked as unknown as FeaturePhotoPoint)
        }

        const snapshot: MapSnapshot = {
          lng: map.getCenter().lng,
          lat: map.getCenter().lat,
          zoom: map.getZoom(),
          bearing: map.getBearing(),
          pitch: map.getPitch(),
          bounds: map.getBounds()!.toArray(),
        }

        const ids = features.map((feature) => feature.properties.id)
        await navigate(`/projects/${projectName}/gallery?ids=${ids.join(',')}`)

        snapshotRef.current = snapshot
      })
    }
  }, [loaded, mapRef, navigate, projectName, snapshotRef])
}
